import { useMemo, useSyncExternalStore } from 'react'

interface ThemeColorDefinition {
	variable: `--${string}`
	fallback: string
}

type ResolvedThemeColors<T extends Record<string, ThemeColorDefinition>> = {
	[K in keyof T]: string
}

const listeners = new Set<() => void>()
let observer: MutationObserver | null = null

const getThemeSnapshot = () => {
	if (typeof document === 'undefined') return 'light'
	return document.documentElement.dataset.theme || 'light'
}

const subscribe = (listener: () => void) => {
	listeners.add(listener)

	if (typeof document !== 'undefined' && !observer) {
		observer = new MutationObserver(() => {
			listeners.forEach((notify) => notify())
		})
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-theme']
		})
	}

	return () => {
		listeners.delete(listener)
		if (listeners.size === 0) {
			observer?.disconnect()
			observer = null
		}
	}
}

/** Resolved app theme, re-rendering when the root data-theme attribute flips. */
export const useResolvedTheme = () =>
	useSyncExternalStore(subscribe, getThemeSnapshot, getThemeSnapshot) as
		| 'light'
		| 'dark'

export const resolveThemeColor = (value: string) => {
	const match = value.match(/^var\((--[^,\s)]+)(?:,\s*([^)]+))?\)$/)
	if (!match || typeof document === 'undefined') return value
	return (
		getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() ||
		match[2]?.trim() ||
		value
	)
}

/**
 * Resolve CSS custom properties for third-party chart/payment APIs that need
 * concrete color strings. DOM styles should use var(--token) directly.
 * Keep definitions outside the component so this memo only re-runs when the
 * root data-theme attribute changes.
 */
export const useThemeColors = <
	T extends Record<string, ThemeColorDefinition>
>(definitions: T): ResolvedThemeColors<T> => {
	const theme = useSyncExternalStore(
		subscribe,
		getThemeSnapshot,
		getThemeSnapshot
	)

	return useMemo(() => {
		const resolved = {} as ResolvedThemeColors<T>
		const styles =
			typeof document === 'undefined'
				? null
				: getComputedStyle(document.documentElement)

		;(Object.keys(definitions) as Array<keyof T>).forEach((key) => {
			const definition = definitions[key]
			resolved[key] = (styles?.getPropertyValue(definition.variable).trim() ||
				definition.fallback) as ResolvedThemeColors<T>[keyof T]
		})

		return resolved
	}, [definitions, theme])
}
